import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Typography
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { apiService } from '../../services/api';
import { makeFields, type FieldKitProps } from './fieldKit';

/** Diagnóstico devolvido pelo billing (somente leitura). */
export type NfseNacionalStatus = {
  effectiveProvider?: 'ipm' | 'nacional';
  ambiente?: 'homologacao' | 'producao';
  ready?: boolean;
  missing?: string[];
  certificate?: {
    configured?: boolean;
    valid?: boolean;
    subject?: string;
    cnpj?: string;
    notAfter?: string;
    daysToExpire?: number;
    error?: string;
  };
};

const OP_SIMP_NAC = [
  { value: '1', label: '1 — Não optante pelo Simples Nacional' },
  { value: '2', label: '2 — Optante MEI' },
  { value: '3', label: '3 — Optante ME/EPP' }
] as const;

const REG_AP_TRIB_SN = [
  { value: '1', label: '1 — Tributos federais e municipais pelo Simples' },
  { value: '2', label: '2 — Federais pelo Simples; ISSQN fora do Simples' },
  { value: '3', label: '3 — Federais e municipais fora do Simples' }
] as const;

const REG_ESP_TRIB = [
  { value: '0', label: '0 — Nenhum' },
  { value: '1', label: '1 — Ato Cooperado (Sociedade Cooperativa)' },
  { value: '2', label: '2 — Estimativa' },
  { value: '3', label: '3 — Microempresa Municipal' },
  { value: '4', label: '4 — Notário ou Registrador' },
  { value: '5', label: '5 — Profissional Autônomo' },
  { value: '6', label: '6 — Sociedade de Profissionais' }
] as const;

const TRIB_ISSQN = [
  { value: '1', label: '1 — Operação tributável' },
  { value: '2', label: '2 — Imunidade' },
  { value: '3', label: '3 — Exportação de serviço' },
  { value: '4', label: '4 — Não incidência' }
] as const;

const TP_RET_ISSQN = [
  { value: '1', label: '1 — ISSQN não retido' },
  { value: '2', label: '2 — Retido pelo tomador' },
  { value: '3', label: '3 — Retido pelo intermediário' }
] as const;

const IND_DEST = [
  { value: '0', label: '0 — O destinatário é o próprio tomador' },
  { value: '1', label: '1 — Destinatário diferente do tomador' }
] as const;

type Props = FieldKitProps & {
  status: NfseNacionalStatus;
  /** Valores iniciais oficiais vindos do billing (somente leitura). */
  defaults: Record<string, unknown>;
  onToggleIbsCbs: (checked: boolean) => void;
  onRestoreDefaults: () => void;
};

/** Campos exclusivos da NFS-e Nacional (SEFIN). */
const NfseNacionalFields: React.FC<Props> = ({
  status,
  defaults,
  onToggleIbsCbs,
  onRestoreDefaults,
  ...kit
}) => {
  const { text, select } = makeFields(kit);
  const { form } = kit;
  const cert = status.certificate || {};
  /** Dica com o padrão oficial lido do billing (nada fixo aqui). */
  const hintDefault = (key: string, base: string) =>
    defaults[key] !== undefined ? `${base} · Padrão oficial: ${String(defaults[key])}` : base;
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; text: string } | null>(null);

  const onTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await apiService.testBillingNfseNacionalConnection();
      const body = (res.data || {}) as { success?: boolean; message?: string };
      setTestResult({
        ok: body.success === true,
        text: body.message || (body.success ? 'Conexão OK' : 'Falha na conexão')
      });
    } catch (e: unknown) {
      const m = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setTestResult({ ok: false, text: m || (e as Error)?.message || 'Falha ao testar conexão' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Emissão pela NFS-e Nacional (Sistema Nacional / SEFIN)
      </Typography>
      <Stack spacing={1.5} sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            variant="outlined"
            color={status.ambiente === 'producao' ? 'warning' : 'info'}
            label={`Ambiente: ${status.ambiente === 'producao' ? 'Produção (NFS-e real)' : 'Homologação (sem valor fiscal)'}`}
          />
          <Chip
            size="small"
            variant="outlined"
            color={cert.valid ? 'success' : cert.configured ? 'error' : 'warning'}
            label={
              cert.configured
                ? cert.valid
                  ? `Certificado A1 OK${cert.daysToExpire != null ? ` · vence em ${cert.daysToExpire} dia(s)` : ''}`
                  : 'Certificado A1 com problema'
                : 'Certificado A1 não configurado'
            }
          />
          <Chip
            size="small"
            variant="outlined"
            color={status.ready ? 'success' : 'warning'}
            label={status.ready ? 'Configuração completa' : 'Configuração incompleta'}
          />
        </Stack>
        {cert.configured && (
          <Typography variant="caption" color="text.secondary">
            Titular: {cert.subject || '—'} · CNPJ do certificado: {cert.cnpj || 'não identificado'}{' '}
            · validade: {cert.notAfter ? new Date(cert.notAfter).toLocaleDateString('pt-BR') : '—'}
          </Typography>
        )}
        {!status.ready && (status.missing?.length ?? 0) > 0 && (
          <Alert severity="warning">
            <Typography variant="body2" gutterBottom>
              Pendências (o Nacional só pode ser ativado, ou agendado por data, com tudo
              preenchido):
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, typography: 'body2' }}>
              {status.missing!.map(m => (
                <li key={m}>{m}</li>
              ))}
            </Box>
          </Alert>
        )}
        <Alert severity="info">
          O certificado A1 (e-CNPJ ICP-Brasil) é lido do servidor do billing —{' '}
          <code>NFSE_NACIONAL_PFX_B64</code> (ou <code>NFSE_NACIONAL_PFX_PATH</code>) e{' '}
          <code>NFSE_NACIONAL_PFX_PASSWORD</code> — e nunca passa pelo navegador. O ambiente segue o
          mesmo critério do IPM: o billing com <code>NODE_ENV=production</code> emite NFS-e{' '}
          <strong>real</strong>; staging/dev usam a produção restrita (sem valor fiscal).
        </Alert>
      </Stack>

      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        Prestador e serviço (DPS)
      </Typography>
      <Grid container spacing={2}>
        {text(
          'nfseNacionalPrestadorCnpj',
          'CNPJ do prestador',
          '14 dígitos — deve ser o mesmo CNPJ do certificado A1',
          { inputProps: { inputMode: 'numeric', maxLength: 18 } }
        )}
        {text('nfseNacionalSerie', 'Série da DPS', '1 a 5 dígitos, diferente de zero', {
          inputProps: { inputMode: 'numeric', maxLength: 5 }
        })}
        {text(
          'nfseNacionalCTribNac',
          'Código de tributação nacional (cTribNac)',
          '6 dígitos: item + subitem + desdobro da LC 116 (confirme com o contador)',
          { inputProps: { inputMode: 'numeric', maxLength: 6 } }
        )}
        {text(
          'nfseNacionalCTribMun',
          'Código de tributação municipal (cTribMun)',
          'Opcional — 3 dígitos, só se o município exigir',
          { inputProps: { inputMode: 'numeric', maxLength: 3 } }
        )}
        {text(
          'nfseNacionalLocalPrestacaoIbge',
          'IBGE do local da prestação',
          'Opcional — vazio usa o IBGE do município (dados comuns)',
          { inputProps: { inputMode: 'numeric', maxLength: 7 } }
        )}
        {text(
          'nfseNacionalPTotTribSn',
          'Total aproximado de tributos — Simples (%)',
          'Opcional (pTotTribSN). Vazio envia “não informado” (indTotTrib=0)'
        )}
        {select(
          'nfseNacionalOpSimpNac',
          'Optante pelo Simples Nacional (opSimpNac)',
          OP_SIMP_NAC,
          'Define como o prestador é tributado'
        )}
        {select(
          'nfseNacionalRegApTribSn',
          'Regime de apuração do Simples (regApTribSN)',
          REG_AP_TRIB_SN,
          'Obrigatório quando for ME/EPP (opSimpNac=3)'
        )}
        {select(
          'nfseNacionalRegEspTrib',
          'Regime especial de tributação (regEspTrib)',
          REG_ESP_TRIB,
          'Em geral “0 — Nenhum”'
        )}
        {select(
          'nfseNacionalTribIssqn',
          'Tributação do ISSQN (tribISSQN)',
          TRIB_ISSQN,
          'Em geral “1 — Operação tributável”'
        )}
        {select(
          'nfseNacionalTpRetIssqn',
          'Retenção do ISSQN (tpRetISSQN)',
          TP_RET_ISSQN,
          'Quem recolhe o ISSQN'
        )}
      </Grid>

      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: 3 }}>
        Reforma tributária (IBS/CBS) na DPS
      </Typography>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Switch
                checked={form.nfseNacionalIncluirIbsCbs}
                onChange={e => onToggleIbsCbs(e.target.checked)}
              />
            }
            label="Incluir o grupo IBSCBS na DPS"
          />
          <Typography variant="caption" color="text.secondary" display="block">
            Usa finNFSe, indFinal, cIndOp, CST e cClassTrib dos dados comuns. Obrigatoriedade plena
            em 01/01/2027 — valide em homologação.
          </Typography>
        </Grid>
        {select(
          'nfseIbsCbsIndDest',
          'IBS/CBS — Indicador de destinatário (indDest)',
          IND_DEST,
          'Exigido pelo grupo IBSCBS da NFS-e Nacional'
        )}
      </Grid>

      <Accordion sx={{ mt: 3 }} disableGutters variant="outlined">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle2">Avançado (URLs, assinatura, numeração)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button size="small" variant="outlined" onClick={onRestoreDefaults}>
              Restaurar padrões oficiais
            </Button>
            <Typography variant="caption" color="text.secondary">
              Preenche URLs, timeout, folga e assinatura com os valores iniciais do billing. Clique
              em “Salvar” para gravar.
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {text(
              'nfseNacionalSefinUrlHomologacao',
              'SEFIN — homologação',
              hintDefault('nfseNacionalSefinUrlHomologacao', 'Obrigatório'),
              {},
              6
            )}
            {text(
              'nfseNacionalSefinUrlProducao',
              'SEFIN — produção',
              hintDefault('nfseNacionalSefinUrlProducao', 'Obrigatório'),
              {},
              6
            )}
            {text(
              'nfseNacionalAdnUrlHomologacao',
              'ADN (DANFSe) — homologação',
              hintDefault('nfseNacionalAdnUrlHomologacao', 'Obrigatório'),
              {},
              6
            )}
            {text(
              'nfseNacionalAdnUrlProducao',
              'ADN (DANFSe) — produção',
              hintDefault('nfseNacionalAdnUrlProducao', 'Obrigatório'),
              {},
              6
            )}
            {select(
              'nfseNacionalSignatureAlgorithm',
              'Algoritmo da assinatura XMLDSIG',
              [
                { value: 'sha256', label: 'RSA-SHA256 (padrão)' },
                { value: 'sha1', label: 'RSA-SHA1 (somente se o Nacional recusar SHA-256)' }
              ],
              hintDefault(
                'nfseNacionalSignatureAlgorithm',
                'Altere só se a homologação indicar erro de assinatura'
              ),
              { allowEmpty: true }
            )}
            {text(
              'nfseNacionalDhEmiSkewSeconds',
              'Folga do relógio em dhEmi (s)',
              hintDefault(
                'nfseNacionalDhEmiSkewSeconds',
                'Segundos subtraídos da hora local (evita “data futura”)'
              ),
              { type: 'number', inputProps: { min: 0, step: 1 } },
              3
            )}
            {text(
              'nfseNacionalTimeoutMs',
              'Timeout HTTP (ms)',
              hintDefault('nfseNacionalTimeoutMs', 'Obrigatório'),
              { type: 'number', inputProps: { min: 1000, step: 1000 } },
              3
            )}
            {text(
              'nfseNacionalDpsSequenciaHomologacao',
              'Último nDPS — homologação',
              'Contador próprio (independente do RPS do IPM). Próximo = valor + 1',
              { type: 'number', inputProps: { min: 0, step: 1 } },
              3
            )}
            {text(
              'nfseNacionalDpsSequenciaProducao',
              'Último nDPS — produção',
              'Ajuste só se já emitiu DPS em produção por outro sistema',
              { type: 'number', inputProps: { min: 0, step: 1 } },
              3
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {testResult && (
        <Alert sx={{ mt: 2 }} severity={testResult.ok ? 'success' : 'error'}>
          {testResult.text}
        </Alert>
      )}
      <Box sx={{ mt: 2 }}>
        <Button variant="outlined" onClick={() => void onTestConnection()} disabled={testing}>
          {testing ? 'Testando…' : 'Testar conexão com o Sistema Nacional'}
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
          Usa a última configuração salva.
        </Typography>
      </Box>
    </>
  );
};

export default NfseNacionalFields;
